/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import {
  Box,
  Divider,
  IconButton,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { NotificationSeverity, type UINotification } from '@poweralpha/common';
import type {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { range, sum, uniqueId } from 'lodash';
import { Fragment, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Emoji } from '../../react-components/Emoji';
import usePANavigate from '../../react-components/navigate/usePANavigate';
import { highlightBackground } from '../../styles';
import { NotificationIcon, type UINotificationsQueryResult } from '../Notifications';
import { getNotifSx } from '../utils';

const AVG_NOTIF_HEIGHT = 75; // px
const NOTIF_LIMIT = 100; // only fetch last 100

const paperSx = (theme: Theme) => ({
  bgcolor: theme.palette.background.default,
  maxHeight: 350,
  width: 450,
  overflowY: 'auto',
  borderRadius: 2,
});

const getMoreSx = (theme: Theme) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: highlightBackground(theme),
  },
});

interface Props {
  data: InfiniteData<UINotificationsQueryResult> | undefined;
  isLoading: boolean;
  isFetching: boolean;
  pageSize: number;
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<UINotificationsQueryResult, unknown>>>;
  handleNotifClick: ({ link, id }: UINotification, redirect?: boolean) => void;
  handleClickAway: () => void;
  handleMarkAllRead: () => void;
}

function NotificationPopperContent({
  data,
  isLoading,
  isFetching,
  pageSize,
  fetchNextPage,
  handleNotifClick,
  handleClickAway,
  handleMarkAllRead,
}: Readonly<Props>) {
  const { navigate, location } = usePANavigate();

  const handleGoToNotifications = useCallback(() => {
    navigate(`/notifications?return=${location.pathname}${location.search}`);
  }, [location.pathname, location.search, navigate]);

  const notificationEntries = useMemo(
    () =>
      (data?.pages ?? []).map((page, i) => (
        <Fragment key={uniqueId('notification-entry')}>
          {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refactor? */}
          {(page?.rows ?? []).map((notif, j) => (
            <>
              <Box
                key={notif.id}
                id={notif.id}
                onClick={() => handleNotifClick(notif)}
                style={{ cursor: 'pointer' }}
                sx={[
                  {
                    color: notif.severity,
                    bgcolor: notif.severity,
                  },
                  (theme) => getNotifSx(theme, notif),
                ]}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    minHeight: 50,
                    padding: 0.5,
                    paddingRight: 1,
                    paddingLeft: 1,
                    alignItems: 'center',
                  }}
                >
                  <NotificationIcon severity={notif.severity} />
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 'fontWeightBold',
                      }}
                    >
                      {notif.subject}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                      }}
                    >
                      {notif.notif_content.length > 150
                        ? `${notif.notif_content.slice(0, 150)}...`
                        : notif.notif_content}
                    </Typography>
                    <Typography
                      hidden={!notif.message}
                      sx={{
                        color: `${notif.severity}.main`,
                        fontSize: 11,
                      }}
                    >
                      {notif.severity === NotificationSeverity.ERROR ? 'Error: ' : 'Comments: '}
                      {notif.message && notif.message.length > 65
                        ? `${notif.message.slice(0, 65)}...`
                        : notif.message}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                      {notif.whenStr}
                      {notif.viewed_at ? '' : ' • Click to view'}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Divider key={uniqueId('divider')} />
              {i === (data?.pages.length ?? 0) - 1 &&
                j === (page?.rows.length ?? 0) - 1 &&
                !isFetching && (
                  <Box
                    onClick={handleGoToNotifications}
                    sx={(theme) => ({ height: 50, ...getMoreSx(theme) })}
                  >
                    <Stack
                      sx={{
                        height: 50,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography>
                        See more notifications <Emoji symbol="🚀" />
                      </Typography>
                    </Stack>
                  </Box>
                )}
            </>
          ))}
        </Fragment>
      )),
    [data?.pages, handleGoToNotifications, handleNotifClick, isFetching],
  );

  const noNotifications = useMemo(() => data?.pages.at(0)?.count === 0, [data?.pages]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e as unknown as { target: { scrollTop: number } };
      if (
        target.scrollTop > pageSize * AVG_NOTIF_HEIGHT * (data?.pages.length ?? 1) &&
        !isFetching
      ) {
        const lastPage = data?.pages.at(-1);
        const currentCount = sum(data?.pages.map(({ rows }) => rows.length));
        if (currentCount < (lastPage?.count ?? 0) && currentCount < NOTIF_LIMIT) {
          fetchNextPage();
        }
      }
    },
    [data?.pages, fetchNextPage, isFetching, pageSize],
  );

  return (
    <Stack>
      <Stack
        direction="row"
        sx={{
          padding: 0.5,
        }}
      >
        <MuiLink
          variant="h5"
          onClick={handleClickAway}
          component={Link}
          to={`/notifications?return=${encodeURIComponent(
            `${location.pathname}${location.search}`,
          )}`}
          sx={{
            paddingLeft: 1,
            paddingTop: 0.5,
            fontWeight: 'fontWeightBold',
          }}
        >
          <u>Notifications</u>
        </MuiLink>
        <Box
          sx={{
            flex: 1,
          }}
        />
        <Tooltip title="Mark all as read">
          <span>
            <IconButton
              color="info"
              onClick={handleMarkAllRead}
              disabled={noNotifications || data?.pages.at(0)?.unread.length === 0}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Divider />
      <Paper onScroll={onScroll} sx={paperSx}>
        {isLoading && (
          <Stack spacing={1}>
            {range(4).map((i) => (
              <Skeleton key={i} height={50} variant="rectangular" />
            ))}
          </Stack>
        )}
        {notificationEntries}
        {isFetching && <Skeleton height={50} variant="rectangular" />}
        {noNotifications && (
          <Box
            sx={{
              padding: 1,
              paddingBottom: 0.5,
            }}
          >
            All clear! <Emoji symbol="🚀" />
          </Box>
        )}
      </Paper>
    </Stack>
  );
}

export default NotificationPopperContent;
